import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { IMAGES, EVENT_SERVICES, waLink } from "@/data/site";
import { MessageCircle, Check } from "lucide-react";
import Seo from "@/components/Seo";

const EVENT_TYPES = {
  weddings: {
    title: "Weddings", h1: "Your day, exactly as you imagined it.",
    intro: "From the first ceremony to the last farewell, we choreograph every function — décor, makeup, photography, catering and entertainment — so your family can simply celebrate.",
    hero: IMAGES.eventMandap,
    services: ["Haldi · Mehndi · Sangeet", "Bridal makeup & styling", "Stage & mandap décor", "Photography & cinematography", "Catering & live counters", "Guest hospitality"],
  },
  "destination-weddings": {
    title: "Destination Weddings", h1: "A wedding worth travelling for.",
    intro: "Palaces, resorts and hill venues — planned end to end. Venue scouting, guest accommodation blocks, transfers, décor and every function, handled by one coordinated team.",
    hero: IMAGES.eventFloral,
    services: ["Venue scouting & booking", "Guest travel & stay blocks", "Transfers & logistics", "Full décor & theming", "Multi-day function planning", "On-ground event crew"],
  },
  celebrations: {
    title: "Celebrations", h1: "Milestones, beautifully marked.",
    intro: "Birthdays, anniversaries and private parties — themes, entertainment, décor, cake and every little touch that turns a gathering into a memory.",
    hero: IMAGES.eventCandles,
    services: ["Theme & concept design", "Décor & florals", "Entertainment & music", "Catering & dining", "Photography", "Return gifts & details"],
  },
  "puja-religious": {
    title: "Puja & Religious Events", h1: "Rituals, held with reverence.",
    intro: "From an intimate home puja to a temple ceremony — décor, priests, prasad, seating and flow, arranged with care and cultural sensitivity.",
    hero: IMAGES.eventDining,
    services: ["Puja & havan setup", "Traditional décor", "Priest coordination", "Prasad & catering", "Seating & sound", "Guest management"],
  },
  "corporate-events": {
    title: "Corporate Events", h1: "Professional. Precise. Produced.",
    intro: "Offsites, conferences, award nights and annual functions — produced with the same care as our destination weddings, at any scale.",
    hero: IMAGES.heroMountain,
    services: ["Conferences & MICE", "Offsites & retreats", "Award nights", "Stage & AV production", "Delegate logistics", "Catering & hospitality"],
  },
};

export default function EventDetail() {
  const { type } = useParams();
  const data = EVENT_TYPES[type];
  if (!data) return <Navigate to="/events" replace />;

  return (
    <div className="bg-[#361E02] text-[#F9EFE1]">
      <Seo title={`${data.title} — Event Management`} description={data.intro.slice(0, 155)} image={data.hero} />
      <div className="relative h-[70vh] min-h-[460px]">
        <img src={data.hero} alt={data.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#361E02]/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-8 lg:px-12">
            <Reveal className="max-w-2xl">
              <p className="eyebrow text-[#D4AF37]">Event Management — a new vertical by VacanzaGo</p>
              <h1 className="font-display text-5xl sm:text-7xl font-medium tracking-tight mt-4 leading-[1.03] text-white">{data.h1}</h1>
              <p className="mt-6 text-lg text-[#F9EFE1]/85">{data.intro}</p>
              <a href={waLink(`Hi Vacanza Go, I'd like to plan a ${data.title} event.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-8 rounded-full btn-orange px-8 py-3.5 text-sm"><MessageCircle size={16} /> Enquire now</a>
            </Reveal>
          </div>
        </div>
      </div>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl text-white mb-10">What we handle</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.services.map((s, i) => (
              <Reveal key={s} delay={(i % 3) * 60}>
                <div className="flex items-center gap-3 rounded-md border border-white/10 bg-white/[0.03] px-5 py-4">
                  <Check size={18} className="text-[#F49E1A] shrink-0" /> <span className="text-[#F9EFE1]/90">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FFF7EC] text-[#2A1808]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <p className="eyebrow">Enquire</p>
            <h2 className="font-display text-4xl font-medium tracking-tight mt-3">Tell us about your {data.title.toLowerCase()}</h2>
            <div className="mt-8">
              <LeadForm source="event" buttonLabel="Send enquiry" waPrefix={`Hi Vacanza Go, I'd like to plan a ${data.title} event.`}
                extraFields={{ initial: { event_type: data.title, event_date: "", guests: "" }, keys: ["event_type", "event_date", "guests"],
                  render: (form, upd, input) => (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input className={input} placeholder="Approx date" value={form.event_date} onChange={upd("event_date")} />
                      <input className={input} placeholder="Guests (approx)" value={form.guests} onChange={upd("guests")} />
                    </div>
                  ) }} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
